export interface OrbAudioReading {
  /** Root-mean-square amplitude, 0..1. */
  rms: number;
  peak: number;
  low: number;
  mid: number;
  high: number;
  /** rms shaped for the orb: gated below the noise floor, boosted, 0..1. */
  level: number;
}

const NOISE_FLOOR = 0.015;
const GAIN = 3.4;

/** Reads the real playback audio from an AnalyserNode and reduces it to a few numbers. */
export class OrbAudioAnalyzer {
  private readonly wave: Uint8Array<ArrayBuffer>;
  private readonly freq: Uint8Array<ArrayBuffer>;

  constructor(private readonly analyser: AnalyserNode) {
    analyser.fftSize = Math.max(analyser.fftSize, 1024);
    analyser.smoothingTimeConstant = 0.55;
    this.wave = new Uint8Array(new ArrayBuffer(analyser.fftSize));
    this.freq = new Uint8Array(new ArrayBuffer(analyser.frequencyBinCount));
  }

  read(): OrbAudioReading {
    this.analyser.getByteTimeDomainData(this.wave);
    this.analyser.getByteFrequencyData(this.freq);

    let sum = 0;
    let peak = 0;
    for (let i = 0; i < this.wave.length; i++) {
      const v = (this.wave[i]! - 128) / 128;
      sum += v * v;
      peak = Math.max(peak, Math.abs(v));
    }
    const rms = Math.sqrt(sum / this.wave.length);

    // Three coarse bands (roughly voice fundamentals, body, sibilance).
    const n = this.freq.length;
    const band = (from: number, to: number) => {
      const a = Math.floor(n * from);
      const b = Math.max(a + 1, Math.floor(n * to));
      let s = 0;
      for (let i = a; i < b; i++) s += this.freq[i]!;
      return s / ((b - a) * 255);
    };

    const gated = Math.max(0, rms - NOISE_FLOOR);
    const level = Math.min(1, Math.sqrt(gated * GAIN));
    return { rms, peak, low: band(0, 0.06), mid: band(0.06, 0.25), high: band(0.25, 0.6), level };
  }
}

export interface PlaybackAnalyser {
  analyser: AnalyserNode;
  context: AudioContext;
  dispose: () => void;
}

/**
 * Taps an <audio>/<video> element that is playing the AI's voice. The audio still reaches the
 * speakers; the analyser only listens. Create it from a user gesture (autoplay rules) and call
 * dispose() when the element is done. A media element can only be wrapped once, so keep the result.
 */
export function createPlaybackAnalyser(media: HTMLMediaElement): PlaybackAnalyser {
  const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
  const context = new Ctx();
  const source = context.createMediaElementSource(media);
  const analyser = context.createAnalyser();
  analyser.fftSize = 1024;
  source.connect(analyser);
  analyser.connect(context.destination);
  return {
    analyser,
    context,
    dispose: () => {
      try { source.disconnect(); analyser.disconnect(); } catch { /* already disconnected */ }
      void context.close();
    },
  };
}
